<template>
  <div>
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <form v-else v-scroll-to-error @submit.prevent="submitForm">
      <div class="user-main-container">
        <div class="user-sidebar-container">
          <div class="user-sidebar">
            <div v-for="item in sidebarItems" 
              :key="item.id" 
              :class="active==item.id ? 'user-list-item-active' : 'user-list-item'" 
              @click.prevent="active=item.id"
            >
              <div class="user-list-item-icon">
                <img :src="`/assets/sidebar-icons/${item.icon}.svg`" alt="">
              </div>
              <div class="user-list-item-title">
                {{ item.title }}
              </div>
            </div>
          </div>
        </div>
        <div style="" class="user-content">
          <div v-if="active===1">
            <h2 class="title-pw">{{ $t('accountSettings', 'Account settings') }}</h2>
            <div class="change-container">
              <user-profile :vs="$v" :initial-values="accountForm" @values="accountForm = $event" />
              <v-flex xs12 class="button">
                <main-button :text="$t('save', 'Save')" depressed @click="$emit('submit')" />
              </v-flex>
            </div>
          </div>
          <div v-if="active===2">
            <h2 class="title-pw">Change password</h2>
            <div class="change-pw-container">
              <text-field
                v-model.trim="passwordForm.oldPassword"
                :validator="$v.passwordForm.oldPassword"
                :error-messages="{ minLength: 'Password must have atleast 5 characters' }"
                label="Old password"
                type="password"
              />
              <text-field
                v-model.trim="passwordForm.newPassword"
                :validator="$v.passwordForm.newPassword"
                :error-messages="{
                  minLength: 'Password must have atleast 5 characters',
                  sameAs: 'Passwords don\'t match',
                }"
                label="New password"
                type="password"
              />
              <text-field
                v-model.trim="passwordForm.confirmPassword"
                :validator="$v.passwordForm.confirmPassword"
                :error-messages="{
                  minLength: 'Password must have atleast 5 characters',
                  sameAs: 'Passwords don\'t match',
                }"
                label="Repeat new password"
                type="password"
              />
              <v-flex xs12 class="button">
                <main-button
                  :text="$t('save', 'Save')"
                  depressed
                  :disabled="!passwordForm.newPassword"
                  type="button"
                  @click="changePassword()"
                />
              </v-flex>
            </div>
          </div>
          <div v-if="active===3">
            <h2 class="title-pw">Notification settings</h2>
            <p class="subtitle">Determine which notifications you want to receive on your email.</p>
            <div class="change-container">
              <notification-settings 
                :notificationData="notificationData.emailNotifications" 
                :userVerified="userVerifiedByCoreEvent" 
              />
              <v-flex xs12 class="button">
                <main-button :text="$t('save', 'Save')" depressed @click.prevent="updateNotificationSettings"/>
              </v-flex>
            </div>
          </div>
        </div>
        <simple-snackbar v-bind.sync="snackbar" />
      </div>
    </form>
  </div>
</template>

<script>
import { GET_LOGGED_USER, UPDATE_USER, CHANGE_PASSWORD, UPDATE_MY_NOTIFICATION_SETTINGS } from '../../graphql/user.graphql'
import { GET_USER } from '../../graphql/auth.graphql'

import { minLength, required, email, sameAs } from 'vuelidate/lib/validators'
import { submitGuard } from './../../utils'
import Spinner from '../shared/spinner'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import UserProfile from './user-profile.vue'
import NotificationSettings from './notification-settings.vue'

export default {
  name: 'user-profile-container',
  components: { UserProfile, SimpleSnackbar, Spinner, NotificationSettings },
  data() {
    return {
      accountForm: {},
      passwordForm: {},
      snackbar: {},
      active: 1,
      sidebarItems: [
        { id: 1, icon: 'account', title: 'PROFILE DETAILS' },
        { id: 2, icon: 'lock-sidebar', title: 'CHANGE PASSWORD' },
        { id: 3, icon: 'notification-sidebar', title: 'NOTIFICATION SETTINGS' },
      ],
      notificationData: {},
      userVerifiedByCoreEvent: false
    }
  },
  validations() {
    return {
      passwordForm: {
        newPassword: {
          minLength: minLength(5),
        },
        confirmPassword: {
          sameAs: sameAs('newPassword'),
        },
      },
      accountForm: {
        firstName: { required },
        lastName: { required },
        email: {
          required,
          email,
        },
      },
    }
  },
  methods: {
    async changePassword() {
      submitGuard(this, async () => {
        const { errors } = await this.$apollo.mutate({
          mutation: CHANGE_PASSWORD,
          variables: {
            oldPassword: this.passwordForm.oldPassword,
            newPassword: this.passwordForm.newPassword,
          },
        })

        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: errors[0].message,
            color: 'error',
          }
        } else {
          this.snackbar = {
            open: true,
            messageProp: 'Successfully changed password',
            color: 'primary',
          }

          this.passwordForm = {}
        }
      })
    },
    async submitForm() {
      submitGuard(this, async () => {
        const { errors } = await this.$apollo.mutate({
          mutation: UPDATE_USER,
          variables: { userInfo: this.accountForm },
          refetchQueries: [{ query: GET_USER }],
        })

        this.snackbar = {
          open: true,
          messageProp: errors ? 'Something went wrong. Please try again' : 'Account settings saved',
          color: errors ? 'error' : 'primary',
        }
      })
    },
    async updateNotificationSettings() {
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_MY_NOTIFICATION_SETTINGS,
        variables: { notificationSettings: this.notificationData },
      })
      this.snackbar = {
        open: true,
        messageProp: errors ? errors[0].message : 'Notification settings updated',
        color: errors ? 'error' : 'primary',
      }
    },
  },
  apollo: {
    me: {
      query: GET_LOGGED_USER,
      manual: true,
      result({ data: { me }, loading }) {
        if (!loading) {
          const { firstName, lastName, email, notificationSettings, isVerifiedByCoreEvent } = me
          this.accountForm = { firstName, lastName, email }
          this.notificationData = notificationSettings
          this.userVerifiedByCoreEvent = isVerifiedByCoreEvent
        }
      },
    },
  },
}
</script>

<style scoped>
.title-pw {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem !important;
  color: #004C8C;
  margin-top: 2.5rem;
  margin-bottom: 1.5rem;
}
.change-container {
  display: flex;
  flex-direction: column;
}
.change-pw-container {
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
}
.button {
  align-self: flex-end;
}
.user-sidebar-container {
  display: flex; 
  flex-direction: column; 
  margin-top: 2.5rem;
  grid-column: 1/2;
}
.user-sidebar {
  position: sticky; 
  top: 80px;
  display: flex;
  padding: 8px 8px 16px 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: 0px 12px 12px 0px;
  background: #F5F5F5;
  max-width: 700px;
  width: 100%;
}
.user-list-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: 48px;
  width: 338px;
  padding: 12px;
  cursor: pointer;
}
.user-list-item-title {
  color: #747474;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: Raleway;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.24px;
}
.user-list-item-icon {
  height: fit-content;
  display: flex;
}
.user-list-item .user-list-item-icon {
  filter: invert(41%) sepia(0%) saturate(15%) hue-rotate(236deg) brightness(93%) contrast(87%);
}
.user-list-item:hover .user-list-item-title {
  color: #666666;
}
.user-list-item:hover .user-list-item-icon {
  filter: none;
}
.user-list-item-active {
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: 48px;
  background-color: #0277bd;
  width: 100%;
  border-radius: 8px;
}
.user-list-item-active .user-list-item-icon {
  height: fit-content;
  display: flex;
  filter: invert(10%) sepia(72%) saturate(1812%) hue-rotate(180deg) brightness(7000%) contrast(830%);
}
.user-list-item-active .user-list-item-title {
  color: white;
}
.subtitle {
  color: #979797;
  font-family: "Source Sans Pro";
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; 
  margin-bottom: 3rem;
  margin-top: 0.25rem;
}
.user-main-container {
  display: grid;
  grid-template-columns: 22rem auto;
  gap: 1.5rem; 
}
.user-content {
  justify-self: start; 
  grid-column: 2/3; max-width: 700px;
  width: 100%;
}

@media only screen and (max-width: 800px) {
  .user-main-container {
    grid-template-columns: 100%;
    padding: 0;
  }
  .user-sidebar-container {
    grid-column: 1;
    position: relative;
    margin: 0 -24px;
  }
  .user-content {
    grid-column: 1;
    width: 100%;
    padding: 0;
  }
}
</style>
